import React, { useEffect, useState } from "react";
import { MainContainer, Section } from "./styles";
import { JsonEditor } from "json-edit-react";
import Typography from "../../../../components/Typography";
import { mockBriefing } from "../../../Enterprise/modules/Briefing/mock";
import { Alert, Divider, notification } from "antd";
import { briefingService } from "../../../../services/modules/briefing";
import useAdmin from "../../hooks/useAdmin";
import Button from "../../../../components/Button";
import ThemeColors from "../../../../themes/colors";
import { Briefing } from "../../../Enterprise/modules/Briefing/types";
import { Row } from "../../../../themes/globalStyle";
import { CrudStatus } from "./types";
import { defaultCrudValues } from "./defaultValues";
import { SmartGoal } from "../../../Enterprise/modules/SmartGoals/types";
import { companyGoalsService } from "../../../../services/modules/companyGoals";
import { Persona } from "../../../Enterprise/modules/Persona/types";
import { mockPersona } from "../../../Enterprise/modules/Persona/mock";
import { personaService } from "../../../../services/modules/persona";
import { BrandPersonality } from "../../../Enterprise/modules/BrandPersonality/types";
import { mockBrandPersonality } from "../../../Enterprise/modules/BrandPersonality/mock";
import { brandPersonalityService } from "../../../../services/modules/brandPersonality";
import { editorialTreeService } from "../../../../services/modules/editorialTree";
import { mockEditorialTree } from "../../../Enterprise/modules/EditorialTree/mock";
import { EditorialTree } from "../../../Enterprise/modules/EditorialTree/types";
import { subAccountService } from "../../../../services/modules/subAccount";

const CRUDContent: React.FC = (): JSX.Element => {
  const { adminState } = useAdmin();

  const mainAccountId = adminState.selectedMainAccountId;

  const [briefingData, setBriefingData] = useState<Briefing>(mockBriefing);
  const [goalsData, setGoalsData] = useState<SmartGoal[]>([]);
  const [personaData, setPersonaData] = useState<Persona>(mockPersona);
  const [brandData, setBrandData] =
    useState<BrandPersonality>(mockBrandPersonality);
  const [editorialTreeData, setEditorialTreeData] =
    useState<EditorialTree>(mockEditorialTree);
  const [crudStatus, setCrudStatus] = useState<CrudStatus>(defaultCrudValues);

  const fetchSubAccount = (id: string) => {
    subAccountService
      .getSubAccountById(id)
      .then((response) => {
        if (response) {
          setCrudStatus((prev) => ({
            ...prev,
            smartGoalDone: response.smart_goal_done ?? false,
          }));
        } else {
          setCrudStatus((prev) => ({ ...prev, smartGoalDone: false }));
        }
      })
      .catch(() => {
        setCrudStatus((prev) => ({ ...prev, smartGoalDone: false }));
      });
  };

  const fetchBriefing = (id: string) => {
    briefingService
      .getBriefing(id, mainAccountId)
      .then((response) => {
        if (response) {
          setBriefingData(response);
          setCrudStatus((prev) => ({ ...prev, briefing: false }));
        } else {
          setCrudStatus((prev) => ({ ...prev, briefing: true }));
        }
      })
      .catch(() => {
        setBriefingData(mockBriefing);
        setCrudStatus((prev) => ({ ...prev, briefing: true }));
      });
  };

  const fetchGoals = (id: string) => {
    companyGoalsService
      .getCompanyGoals(id, mainAccountId)
      .then((response) => {
        if (response) {
          setGoalsData(response);
          setCrudStatus((prev) => ({ ...prev, companyGoals: false }));
        } else {
          setCrudStatus((prev) => ({ ...prev, companyGoals: true }));
        }
      })
      .catch(() => {
        setGoalsData([]);
        setCrudStatus((prev) => ({ ...prev, companyGoals: true }));
      });
  };

  const fetchPersona = (id: string) => {
    personaService
      .getPersona(id, mainAccountId)
      .then((response) => {
        if (response) {
          setPersonaData(response);
          setCrudStatus((prev) => ({ ...prev, persona: false }));
        } else {
          setCrudStatus((prev) => ({ ...prev, persona: true }));
        }
      })
      .catch(() => {
        setPersonaData(mockPersona);
        setCrudStatus((prev) => ({ ...prev, persona: true }));
      });
  };

  const fetchBrandPersonality = (id: string) => {
    brandPersonalityService
      .getBrandPersonality(id, mainAccountId)
      .then((response) => {
        if (response) {
          setBrandData(response);
          setCrudStatus((prev) => ({ ...prev, brandPersonality: false }));
        } else {
          setCrudStatus((prev) => ({ ...prev, brandPersonality: true }));
        }
      })
      .catch(() => {
        setBrandData(mockBrandPersonality);
        setCrudStatus((prev) => ({ ...prev, brandPersonality: true }));
      });
  };

  const fetchEditorialTree = (id: string) => {
    editorialTreeService
      .getEditorialTree(id, mainAccountId)
      .then((response) => {
        if (response) {
          setEditorialTreeData(response);
          setCrudStatus((prev) => ({ ...prev, editorialTree: false }));
        } else {
          setCrudStatus((prev) => ({ ...prev, editorialTree: true }));
        }
      })
      .catch(() => {
        setEditorialTreeData(mockEditorialTree);
        setCrudStatus((prev) => ({ ...prev, editorialTree: true }));
      });
  };

  const createSection = (
    service: (id: string, data: any, mainAccountId?: string) => Promise<any>,
    id: string,
    data: any,
    title: string
  ) => {
    service(id, data, mainAccountId).then(() =>
      notification.success({
        message: "Sucesso",
        description: `${title} criado com sucesso!`,
      })
    );
  };

  const deleteSection = (
    service: (id: string, mainAccountId?: string) => Promise<any>,
    id: string,
    title: string
  ) => {
    service(id, mainAccountId).then(() =>
      notification.success({
        message: "Sucesso",
        description: `${title} deletada com sucesso!`,
      })
    );
  };

  useEffect(() => {
    if (adminState.selectedEnterprise) {
      fetchBriefing(adminState.selectedEnterprise);
      fetchGoals(adminState.selectedEnterprise);
      fetchPersona(adminState.selectedEnterprise);
      fetchBrandPersonality(adminState.selectedEnterprise);
      fetchEditorialTree(adminState.selectedEnterprise);
      fetchSubAccount(adminState.selectedEnterprise);
    }
  }, [adminState.selectedEnterprise]);

  return (
    <MainContainer>
      <Section>
        <Typography
          text={"Briefing"}
          size="huge"
          color="Black500"
          weight={700}
        />
        {crudStatus.briefing && (
          <Alert
            style={{ width: "fit-content" }}
            message="Briefing não criado! Foi gerado um briefing automático como exemplo."
            type="warning"
            showIcon
          />
        )}
        <JsonEditor
          data={briefingData}
          theme="monoDark"
          collapse={1}
          setData={(data) => setBriefingData(data as Briefing)}
        />
        <Row>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black300}
            onClick={() =>
              adminState.selectedEnterprise &&
              deleteSection(
                briefingService.deleteBriefing,
                adminState.selectedEnterprise,
                "Briefing"
              )
            }
          >
            <Typography
              text="Deletar"
              size="medium"
              color="Bg300"
              weight={400}
            />
          </Button>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black500}
            onClick={() =>
              adminState.selectedEnterprise &&
              createSection(
                briefingService.createBriefing,
                adminState.selectedEnterprise,
                briefingData,
                "Briefing"
              )
            }
          >
            <Typography text="Criar" size="medium" color="Bg300" weight={400} />
          </Button>
        </Row>
      </Section>
      <Divider />
      <Section>
        {crudStatus.smartGoalDone && (
          <Alert
            style={{ width: "fit-content" }}
            message="Objetivos concluídos!"
            type="info"
            showIcon
          />
        )}
        <Typography
          text={"Objetivos"}
          size="huge"
          color="Black500"
          weight={700}
        />
        {crudStatus.companyGoals && (
          <Alert
            style={{ width: "fit-content" }}
            message="Objetivos não criado! Foram gerados objetivos automáticos como exemplo."
            type="warning"
            showIcon
          />
        )}
        <JsonEditor
          data={goalsData}
          theme="monoDark"
          collapse={1}
          restrictEdit
        />
      </Section>
      <Divider />
      <Section>
        <Typography
          text={"Persona"}
          size="huge"
          color="Black500"
          weight={700}
        />
        {crudStatus.persona && (
          <Alert
            style={{ width: "fit-content" }}
            message="Persona não criada! Foram gerados persona automáticos como exemplo."
            type="warning"
            showIcon
          />
        )}
        <JsonEditor
          data={personaData}
          theme="monoDark"
          collapse={1}
          setData={(data) => setPersonaData(data as Persona)}
        />
        <Row>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black300}
            onClick={() =>
              adminState.selectedEnterprise &&
              deleteSection(
                personaService.deletePersona,
                adminState.selectedEnterprise,
                "Persona"
              )
            }
          >
            <Typography
              text="Deletar"
              size="medium"
              color="Bg300"
              weight={400}
            />
          </Button>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black500}
            onClick={() =>
              adminState.selectedEnterprise &&
              createSection(
                personaService.createPersona,
                adminState.selectedEnterprise,
                personaData,
                "Persona"
              )
            }
          >
            <Typography text="Criar" size="medium" color="Bg300" weight={400} />
          </Button>
        </Row>
      </Section>
      <Divider />
      <Section>
        <Typography
          text={"Tom e Voz"}
          size="huge"
          color="Black500"
          weight={700}
        />
        {crudStatus.brandPersonality && (
          <Alert
            style={{ width: "fit-content" }}
            message="Tom e voz não criado! Foram gerados tom e voz automáticos como exemplo."
            type="warning"
            showIcon
          />
        )}
        <JsonEditor
          data={brandData}
          theme="monoDark"
          collapse={1}
          setData={(data) => setBrandData(data as BrandPersonality)}
        />
        <Row>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black300}
            onClick={() =>
              adminState.selectedEnterprise &&
              deleteSection(
                brandPersonalityService.deleteBrandPersonality,
                adminState.selectedEnterprise,
                "Tom e Voz"
              )
            }
          >
            <Typography
              text="Deletar"
              size="medium"
              color="Bg300"
              weight={400}
            />
          </Button>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black500}
            onClick={() =>
              adminState.selectedEnterprise &&
              createSection(
                brandPersonalityService.createBrandPersonality,
                adminState.selectedEnterprise,
                brandData,
                "Tom e Voz"
              )
            }
          >
            <Typography text="Criar" size="medium" color="Bg300" weight={400} />
          </Button>
        </Row>
      </Section>
      <Divider />
      <Section>
        <Typography
          text={"Árvore Editorial"}
          size="huge"
          color="Black500"
          weight={700}
        />
        {crudStatus.editorialTree && (
          <Alert
            style={{ width: "fit-content" }}
            message="Árvore Editorial não criada! Foram gerados Árvore Editorial automáticos como exemplo."
            type="warning"
            showIcon
          />
        )}
        <JsonEditor
          data={editorialTreeData}
          theme="monoDark"
          collapse={1}
          setData={(data) => setEditorialTreeData(data as EditorialTree)}
        />
        <Row>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black300}
            onClick={() =>
              adminState.selectedEnterprise &&
              deleteSection(
                editorialTreeService.deleteEditorialTree,
                adminState.selectedEnterprise,
                "Árvore Editorial"
              )
            }
          >
            <Typography
              text="Deletar"
              size="medium"
              color="Bg300"
              weight={400}
            />
          </Button>
          <Button
            size="middle"
            borderRadius="0.5rem"
            style={{ width: "fit-content" }}
            backgroundColor={ThemeColors.Black500}
            onClick={() =>
              adminState.selectedEnterprise &&
              createSection(
                editorialTreeService.createEditorialTree,
                adminState.selectedEnterprise,
                editorialTreeData,
                "Árvore Editorial"
              )
            }
          >
            <Typography text="Criar" size="medium" color="Bg300" weight={400} />
          </Button>
        </Row>
      </Section>
    </MainContainer>
  );
};

export default CRUDContent;
